import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
  CheckCircle2, Calendar, MessageCircle, Heart, Send, Activity,
  Loader2, User, Mail, Shield, Clock, Briefcase
} from 'lucide-react';

const TYPE_CONFIG = {
  task_created: { icon: CheckCircle2, label: 'Created task', color: 'text-cyan-600 bg-cyan-50 dark:bg-cyan-900/20' },
  task_claimed: { icon: Briefcase, label: 'Claimed task', color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' },
  event: { icon: Calendar, label: 'Created event', color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
  post: { icon: MessageCircle, label: 'Posted', color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' },
  note_sent: { icon: Send, label: 'Sent note', color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/20' },
  note_received: { icon: Heart, label: 'Received note', color: 'text-pink-600 bg-pink-50 dark:bg-pink-900/20' },
  care_pulse: { icon: Activity, label: 'Care Pulse', color: 'text-violet-600 bg-violet-50 dark:bg-violet-900/20' },
};

const StatBadge = ({ label, value }) => (
  <div className="text-center px-3 py-2 bg-muted/40 rounded-xl">
    <p className="text-lg font-bold text-foreground">{value}</p>
    <p className="text-[10px] text-muted-foreground">{label}</p>
  </div>
);

const UserActivityDialog = ({ open, onOpenChange, userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && userId) {
      setLoading(true);
      axios.get(`${API}/admin/users/${userId}/activity`)
        .then(res => setData(res.data))
        .catch(() => toast.error('Failed to load user activity'))
        .finally(() => setLoading(false));
    }
  }, [open, userId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface-card max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>User Activity</DialogTitle>
          <DialogDescription>Usage logs and activity timeline</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5 mt-2">
            {/* User Info */}
            <div className="flex items-center gap-4 pb-4 border-b border-border">
              <div className="w-12 h-12 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <User className="w-6 h-6 text-cyan-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-foreground">{data.user.name || 'Unnamed'}</h3>
                <div className="flex items-center gap-3 flex-wrap mt-0.5">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="w-3 h-3" /> {data.user.email}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${data.user.role === 'patient' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                    {data.user.role || 'user'}
                  </span>
                  {data.user.is_admin && <span className="flex items-center gap-1 text-xs text-amber-600"><Shield className="w-3 h-3" /> Admin</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Joined {data.user.created_at ? new Date(data.user.created_at).toLocaleDateString() : '—'}</span>
                  {data.user.signup_code && <span className="font-mono font-bold text-cyan-600" data-testid="user-signup-code">Signup code: {data.user.signup_code}</span>}
                  {data.subscription && <span className="font-medium text-cyan-600">Plan: {data.subscription.plan}</span>}
                </div>
                {data.circles.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {data.circles.map(c => (
                      <span key={c.id} className="text-[10px] bg-muted/60 text-muted-foreground px-2 py-0.5 rounded-full">{c.name}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              <StatBadge label="Tasks Created" value={data.summary.tasks_created} />
              <StatBadge label="Tasks Claimed" value={data.summary.tasks_claimed} />
              <StatBadge label="Events" value={data.summary.events_created} />
              <StatBadge label="Posts" value={data.summary.posts} />
              <StatBadge label="Notes Sent" value={data.summary.notes_sent} />
              <StatBadge label="Notes Got" value={data.summary.notes_received} />
              <StatBadge label="Mood Logs" value={data.summary.care_pulses} />
            </div>

            {/* Timeline */}
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-foreground mb-2">Activity Timeline</h4>
              {data.timeline.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-6">No activity yet</p>
              )}
              {data.timeline.map((item, i) => {
                const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.post;
                const Icon = config.icon;
                const key = `${item.type}-${item.date}-${i}`;
                return (
                  <div key={key} className="flex items-start gap-3 py-2.5 border-b border-border/40 last:border-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase">{config.label}</span>
                        {item.date && <span className="text-[10px] text-muted-foreground/60">{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
                      </div>
                      <p className="text-sm text-foreground truncate">{item.title}</p>
                      {item.detail && <p className="text-xs text-muted-foreground truncate">{item.detail}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserActivityDialog;
